import { Amplify } from 'aws-amplify';

interface AppConfig {
  userPoolId: string;
  userPoolClientId: string;
  region: string;
  apiUrl: string;
}

let resolvedApiUrl = '';

export function getApiUrl(): string {
  return resolvedApiUrl;
}

async function loadConfig(): Promise<AppConfig> {
  // Local dev only: VITE_ vars are baked in at build time by Vite.
  // WARNING: never set VITE_COGNITO_USER_POOL_ID in CI/deploy build steps —
  // Amplify parses the region from the pool ID, so a fake value like
  // "runtime_config" produces region="runtime" and breaks all Cognito calls.
  if (import.meta.env.VITE_COGNITO_USER_POOL_ID) {
    if (!import.meta.env.VITE_API_URL) {
      throw new Error(
        'Missing API URL.\n\n' +
        'Add VITE_API_URL to apps/web/.env.local\n' +
        '  (get it from: cd infra/terraform/environments/app && terraform output weights_api_url)\n' +
        'Then restart the dev server.',
      );
    }
    return {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
      apiUrl: import.meta.env.VITE_API_URL,
    };
  }

  // In local development, .env.local is required. Fetching /config.json will
  // always 404 from the Vite dev server, so give a clear actionable message.
  if (import.meta.env.DEV) {
    throw new Error(
      'Missing Cognito config.\n\n' +
      '1. Copy apps/web/.env.example to apps/web/.env.local\n' +
      '2. Fill in VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID, and VITE_API_URL\n' +
      '   (get these from: cd infra/terraform/environments/app && terraform output)\n' +
      '3. Restart the dev server',
    );
  }

  // Deployed envs: fetch runtime config placed in S3 bucket by the CI deploy step.
  // This allows the same build artifact to be promoted from preprod → prod
  // without a rebuild, since env-specific values come from config.json at runtime.
  const res = await fetch('/config.json');
  if (!res.ok) {
    throw new Error(`Failed to load /config.json: ${res.status}`);
  }
  return res.json() as Promise<AppConfig>;
}

export const configReady: Promise<void> = loadConfig().then((config) => {
  resolvedApiUrl = config.apiUrl;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
      },
    },
  });
});
